/**
 * The office's confirm queue: jobs a field hand has declared finished (API
 * 0337) that nobody has closed yet.
 *
 * ─── WHY THIS EXISTS AND WHY IT IS NOT OPTIONAL ─────────────────────────────
 *
 * Before 0337 nothing told the office a job was done, because the office was
 * the actor — the crew SMS tells the crew what the office just did, and the
 * Slack post is the office telling itself. A hand can now say "the work is
 * finished", and this is where that lands.
 *
 * THE API ALSO POSTS TO SLACK on the tap, and that is deliberately NOT the
 * mechanism. Most tenants have no `slack_webhook_url` configured and
 * `sendSlackNotification` returns silently when it is unset, so for them the
 * Slack half does not exist at all. A post also scrolls away. THIS is the
 * durable half: a work list that persists until somebody actions it. If this
 * component is not on screen, the feature has no office-facing side.
 *
 * ─── RENDERS NOTHING WHEN EMPTY ─────────────────────────────────────────────
 *
 * Deliberate: on most days no hand has an outstanding declaration, and a
 * permanent "0 jobs awaiting confirmation" panel is dashboard furniture that
 * teaches people to stop looking at that region. It appears when there is
 * something to do. Same instinct as OpsAlerts above it.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Package } from 'lucide-react';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Roles the API's confirm-queue endpoint accepts. A field worker gets 403, so
 * we do not ask on their behalf — this is about not firing a request that is
 * certain to fail, not about access control, which is the server's job.
 */
const OFFICE_ROLES = new Set(['admin', 'office_staff', 'supervisor', 'dispatcher']);

interface QueuedJob {
  id: string;
  job_number: string | null;
  status: string;
  rig_name: string | null;
  well_name: string | null;
  customer_name: string | null;
  work_finished_at: string | null;
  finished_by_first_name: string | null;
  finished_by_last_name: string | null;
  equipment_outstanding: number | null;
}

/**
 * "Dale F." — or "a crew member" when the user row is gone.
 *
 * `work_finished_by` has no foreign key and `users` has no soft delete, so a
 * deleted hand is a real state rather than an error (see API migration 0337).
 * The API LEFT JOINs precisely so the JOB still appears; this is the display
 * side of the same decision. Falling back to a blank would make the row read
 * as broken when it is merely historical.
 */
function finishedBy(j: QueuedJob): string {
  const first = j.finished_by_first_name?.trim();
  const last = j.finished_by_last_name?.trim();
  if (!first && !last) return 'a crew member';
  return [first, last ? `${last[0]}.` : ''].filter(Boolean).join(' ');
}

/**
 * "2h ago" / "yesterday" — how long the office has been sitting on it.
 *
 * ABSENT RENDERS AS AN EM DASH, never "just now". A missing timestamp on a row
 * that is in this queue BECAUSE it has one means we failed to read it, and
 * "just now" would be a confident wrong answer about how stale the request is.
 */
function since(iso: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

/**
 * `bare` drops the card frame and its icon: inside the Overview's Action Queue
 * the rows sit under that panel's heading as one section of it.
 */
export default function WorkFinishedQueue({ bare = false }: { bare?: boolean } = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOffice = OFFICE_ROLES.has(String((user as any)?.role ?? ''));

  const { data } = useQuery({
    queryKey: ['work-finished-queue'],
    queryFn: async () => {
      const res = await api.dispatch.workFinishedJobs();
      const body = res.data;
      // Explicit unwrap. `body?.data || body` would fall through to the ENVELOPE
      // object when the API answers { success: true, data: null } — truthy, so
      // every field then reads undefined and the queue renders rows of blanks.
      // 62 call sites in this repo carry that bug; this is not going to be 63.
      return (body && typeof body === 'object' && 'data' in body ? body.data : body) ?? [];
    },
    enabled: isOffice,
  });

  const jobs: QueuedJob[] = Array.isArray(data) ? data : [];
  if (!isOffice || jobs.length === 0) return null;

  return (
    <div
      data-testid="work-finished-queue"
      className={bare ? '' : 'bg-fieldiq-card border border-fieldiq-border rounded-fi-panel p-5'}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-fi-control bg-fi-signal-green flex items-center justify-center flex-shrink-0">
          <CheckCircle2 className="w-4 h-4 text-fi-signal-ink" aria-hidden="true" />
        </div>
        <div>
          <h3 className="text-fieldiq-text font-semibold text-sm">Work finished — awaiting your confirmation</h3>
          <p className="text-fieldiq-muted text-xs">
            {jobs.length === 1 ? 'A crew has' : `${jobs.length} crews have`} reported the work done. Close the job to confirm.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {jobs.map((j) => (
          <button
            key={j.id}
            onClick={() => navigate(`/jobs/${j.id}`)}
            className="w-full text-left bg-fieldiq-dark/40 border border-fieldiq-border rounded-fi-control p-3 flex items-center gap-3 hover:bg-fieldiq-dark/70 transition-colors active:scale-[0.99]"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-fieldiq-text font-semibold text-sm">{j.job_number ?? '—'}</span>
                <span className="text-fieldiq-muted text-xs">{since(j.work_finished_at)}</span>
              </div>
              <p className="text-fieldiq-muted text-xs truncate mt-0.5">
                {[j.customer_name, j.rig_name, j.well_name].filter(Boolean).join(' · ') || '—'}
              </p>
              <p className="text-fieldiq-text text-xs mt-1">
                Reported by {finishedBy(j)}
              </p>
            </div>

            {/*
              Equipment still out. REPORTED, NOT ENFORCED — completion does not
              demand a disposition from this door and the field door does not
              either, so this is the office seeing what is outstanding as they
              close, not a gate. Absent is `—`; zero is a real, different answer
              and gets no chip at all.
            */}
            {typeof j.equipment_outstanding === 'number' && j.equipment_outstanding > 0 && (
              <span className="flex items-center gap-1 text-fi-signal-ink text-xs font-bold bg-fi-signal-amber border border-black/20 rounded-fi-pill px-2 py-1 flex-shrink-0">
                <Package className="w-3 h-3" />
                {j.equipment_outstanding} out
              </span>
            )}

            <ArrowRight className="w-4 h-4 text-fieldiq-muted flex-shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}
